import { useState, useMemo } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Tag, Transaction, TagMap } from '../types';
import { buildTagHierarchy, HierarchicalTag } from '../utils/tagUtils';
import TransactionWithNarration from './TransactionWithNarration';
import Badge from './Badge';

interface TagSelectorProps {
  onSelectTag: (tagName: string | null) => void;
  availableTags: Tag[];
  tagMap: TagMap;
  currentTagName?: string | null;
  transaction?: Transaction;
  disabled?: boolean;
}

function TagSelector({ onSelectTag, availableTags, tagMap, currentTagName, transaction, disabled = false }: TagSelectorProps) {
  const selectedTagName = currentTagName ?? null;
  const [searchTerm, setSearchTerm] = useState<string>('');

  const tagHierarchy = useMemo(() => buildTagHierarchy(availableTags), [availableTags]);

  const filteredHierarchy = useMemo(() => {
    if (!searchTerm) return tagHierarchy;
    const lowerSearchTerm = searchTerm.toLowerCase();

    const filter = (tags: HierarchicalTag[]): HierarchicalTag[] => {
      return tags.reduce((acc, tag) => {
        const matchingChildren = tag.children.filter(child =>
          child.name.toLowerCase().includes(lowerSearchTerm)
        );
        const parentMatches = tag.name.toLowerCase().includes(lowerSearchTerm);

        if (parentMatches || matchingChildren.length > 0) {
          acc.push({ ...tag, children: parentMatches ? tag.children : matchingChildren });
        }
        return acc;
      }, [] as HierarchicalTag[]);
    };

    return filter(tagHierarchy);
  }, [searchTerm, tagHierarchy]);

  const handleSelect = (tagName: string | null) => {
    if (disabled) return;
    if (tagName === selectedTagName) return;
    onSelectTag(tagName);
  };

  return (
    <div className="flex flex-col h-full">
      {transaction && (
        <div className="mx-4">
          <TransactionWithNarration transaction={transaction} tagMap={tagMap} />
        </div>
      )}

      <div className="relative mb-4 flex-shrink-0 mx-4">
        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={disabled}
          className="w-full bg-secondary border border-input rounded-lg pl-10 pr-4 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>

      <div className="flex-grow overflow-y-auto pt-1 space-y-5 p-4">
        {filteredHierarchy.map((parentTag) => (
          <div className="flex flex-col bg-secondary rounded-xl" key={parentTag.name}>
            <div className={`flex items-center mb-1 w-full ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`} onClick={() => handleSelect(parentTag.name)}>
              <input
                type="radio"
                name="tagSelection"
                id={`tag-${parentTag.name}`}
                checked={selectedTagName === parentTag.name || parentTag.children.some((c) => c.name === selectedTagName)}
                readOnly
                disabled={disabled}
                className="mr-3 ml-4 h-5 w-5 text-primary border-border focus:ring-ring focus:ring-offset-background bg-secondary accent-secondary-foreground cursor-pointer disabled:cursor-not-allowed"
              />
              <label htmlFor={`tag-${parentTag.name}`} className={`text-base pt-4 pb-4 font-semibold text-foreground flex-grow ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                {parentTag.name}
              </label>
            </div>

            {parentTag.children.length > 0 && (
              <div className="flex items-center space-x-2 overflow-x-auto whitespace-nowrap px-5 pb-4">
                {parentTag.children.map((childTag) => {
                  const isSelected = selectedTagName === childTag.name;
                  return (
                    <Badge
                      as="button"
                      key={childTag.name}
                      onClick={() => handleSelect(childTag.name)}
                      variant={isSelected ? 'selected' : 'default'}
                      disabled={disabled}
                      className="px-3 py-2 transition-colors duration-150 flex items-center space-x-1.5 flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span>{childTag.name}</span>
                    </Badge>
                  );
                })}

                <Badge
                  as="button"
                  key={`${parentTag.name}-others`}
                  onClick={() => handleSelect(parentTag.name)}
                  variant={selectedTagName === parentTag.name ? 'selected' : 'default'}
                  disabled={disabled}
                  className="px-2.5 py-1.5 transition-colors duration-150 flex items-center space-x-1.5 flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span>Others</span>
                </Badge>

                <div className="flex-shrink-0 w-1"></div>
              </div>
            )}
          </div>
        ))}

        <div className={`flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`} onClick={() => handleSelect(null)}>
          <input
            type="radio"
            name="tagSelection"
            id="tag-none"
            checked={selectedTagName === null}
            onChange={() => handleSelect(null)}
            disabled={disabled}
            className="mr-3 ml-4 h-5 w-5 text-primary border-border focus:ring-ring focus:ring-offset-background bg-secondary accent-secondary-foreground cursor-pointer disabled:cursor-not-allowed"
          />
          <label htmlFor="tag-none" className={`text-base pt-4 pb-4 font-semibold text-foreground flex-grow ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            No Tag
          </label>
        </div>
      </div>
    </div>
  );
}

export default TagSelector;
